import { useState, useEffect, useRef } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const guideLinks = [
    { label: "Co to jest Spreadsheet?", url: "/co-to-jest-kakobuy-spreadsheet" },
    { label: "Jak Zamawiać?", url: "/jak-zamawiac-kakobuy" },
    { label: "Kakobuy vs Pandabuy", url: "/kakobuy-vs-pandabuy" },
    { label: "Bezpieczne Zakupy", url: "/bezpieczne-zakupy-chiny" },
    { label: "Darmowy Spreadsheet", url: "/darmowy-kakobuy-spreadsheet" },
    { label: "Najlepsze Buty", url: "/najlepsze-buty-kakobuy" },
    { label: "Streetwear Guide", url: "/streetwear-kakobuy-spreadsheet" },
    { label: "Bezpieczeństwo", url: "/czy-kakobuy-jest-bezpieczny" },
    { label: "Kakobuy Finds", url: "/kakobuy-finds" },
    { label: "Kakobuy Links", url: "/kakobuy-links" },
    { label: "Kakobuy Yupoo", url: "/kakobuy-yupoo" },
    { label: "Agent Ranking 2026", url: "/najlepszy-agent-zakupowy-2026" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50 shadow-sm"
          : "bg-black/30 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between h-[72px]">
        <RouterLink
          to="/"
          className={`text-lg md:text-xl font-heading font-bold tracking-tight whitespace-nowrap cursor-pointer drop-shadow-sm ${
            scrolled ? "text-foreground-950" : "text-white"
          }`}
        >
          Kakobuy Spreadsheet
        </RouterLink>

        <div className="hidden md:flex items-center gap-6">
          <RouterLink
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              scrolled
                ? "text-foreground-700 hover:text-foreground-950"
                : "text-white/90 hover:text-white"
            }`}
          >
            Strona Główna
          </RouterLink>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => { setDropdownOpen(!dropdownOpen); setGuidesDropdownOpen(false); }}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950"
                  : "text-white/90 hover:text-white"
              }`}
            >
              Kategorie
              <i className={`ri-arrow-down-s-line text-base transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-3 left-0 bg-background-50 rounded-xl border border-background-200 shadow-xl py-3 w-56 max-h-[420px] overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-base text-primary-500`}></i>
                    </span>
                    {cat.namePl}
                  </a>
                ))}
              </div>
            )}
          </div>

          <RouterLink
            to="/co-to-jest-kakobuy-spreadsheet"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              scrolled
                ? "text-foreground-700 hover:text-foreground-950"
                : "text-white/90 hover:text-white"
            }`}
          >
            Artykuły
          </RouterLink>

          <div className="relative" ref={guidesRef}>
            <button
              onClick={() => { setGuidesDropdownOpen(!guidesDropdownOpen); setDropdownOpen(false); }}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950"
                  : "text-white/90 hover:text-white"
              }`}
            >
              Poradniki Zakupowe
              <i className={`ri-arrow-down-s-line text-base transition-transform ${guidesDropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {guidesDropdownOpen && (
              <div className="absolute top-full mt-3 left-0 bg-background-50 rounded-xl border border-background-200 shadow-xl py-3 w-64 max-h-[380px] overflow-y-auto z-50">
                {guideLinks.map((link) => (
                  <RouterLink
                    key={link.url}
                    to={link.url}
                    onClick={() => setGuidesDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-book-read-line text-base text-accent-500"></i>
                    </span>
                    {link.label}
                  </RouterLink>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`px-5 py-2 text-sm font-semibold rounded-full border transition-all whitespace-nowrap cursor-pointer ${
              scrolled
                ? "border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white"
                : "border-white text-white hover:bg-white hover:text-foreground-950"
            }`}
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
            scrolled ? "text-foreground-950" : "text-white drop-shadow-sm"
          }`}
        >
          <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 flex flex-col gap-2 max-h-[80vh] overflow-y-auto">
          <RouterLink
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 text-sm font-semibold text-foreground-950 hover:text-primary-500 py-3 px-2 rounded-lg cursor-pointer"
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <i className="ri-home-4-line text-lg text-primary-500"></i>
            </span>
            Strona Główna
          </RouterLink>

          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="flex items-center justify-between text-sm font-semibold text-foreground-950 py-3 px-2 rounded-lg cursor-pointer w-full"
          >
            <span className="flex items-center gap-3">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-grid-line text-lg text-primary-500"></i>
              </span>
              Kategorie
            </span>
            <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? "rotate-180" : ""}`}></i>
          </button>

          {mobileCatOpen && (
            <div className="grid grid-cols-2 gap-2 pl-10 pr-2 pb-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-2 py-2 text-xs text-foreground-600 bg-background-100 rounded-lg hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-xs text-primary-500`}></i>
                  {cat.namePl}
                </a>
              ))}
            </div>
          )}

          <RouterLink
            to="/co-to-jest-kakobuy-spreadsheet"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 text-sm font-semibold text-foreground-950 hover:text-primary-500 py-3 px-2 rounded-lg cursor-pointer"
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <i className="ri-article-line text-lg text-accent-500"></i>
            </span>
            Artykuły
          </RouterLink>

          <div className="border-t border-background-200 pt-2 mt-1">
            <p className="text-xs font-semibold text-foreground-400 uppercase mb-2 px-2">Poradniki Zakupowe</p>
            <div className="grid grid-cols-2 gap-1.5 px-2">
              {guideLinks.map((link) => (
                <RouterLink
                  key={link.url}
                  to={link.url}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-1.5 px-2 py-2 text-xs text-foreground-600 bg-background-100 rounded-lg hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-read-line text-xs text-accent-500"></i>
                  {link.label}
                </RouterLink>
              ))}
            </div>
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center px-5 py-3 mt-3 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Buy It Now
          </a>
        </div>
      )}
    </nav>
  );
}